import type { CrmActivity, CrmDealDetail } from '@ip/shared-types';
import { STALE_DAYS, daysBetween, daysSinceActivity, isClosedDeal, isPastDue, type StageIndex } from './dealMath';
import type { Tone } from './tones';

/**
 * "Co dalej?" — a derived, first-match-wins recommendation for an open deal.
 * Pure and React-free on purpose (no test runner in the frontend; the rules
 * are meant to be read as a table).
 */

export type NextStepAction =
  | 'moveDate'
  | 'won'
  | 'lost'
  | 'call'
  | 'logCall'
  | 'enterValue'
  | 'setDate'
  | 'moveNext';

export interface NextStep {
  tone: Tone;
  /** i18n key under `crm.deals.nextStep.*`. */
  textKey: string;
  params: Record<string, string | number>;
  actions: NextStepAction[];
  /** For `moveNext`: the stage to move to (undefined when the deal is in the last open stage). */
  nextStageId?: string;
}

export interface NextStepInput {
  deal: CrmDealDetail;
  activities: ReadonlyArray<CrmActivity>;
  stageIndex: StageIndex;
  now: Date | string;
  /** Primary contact's display name and whether it has a phone number. */
  contactName?: string | null;
  contactHasPhone?: boolean;
}

/** Newest activity across the list and the deal's own `lastActivityAt`. */
function latestActivityAt(
  deal: Pick<CrmDealDetail, 'lastActivityAt'>,
  activities: ReadonlyArray<CrmActivity>,
): string | null {
  let latest = deal.lastActivityAt;
  for (const activity of activities) {
    if (!latest || activity.occurredAt > latest) latest = activity.occurredAt;
  }
  return latest;
}

export function nextStepFor(input: NextStepInput): NextStep | null {
  const { deal, activities, stageIndex, now } = input;
  if (isClosedDeal(deal, stageIndex.byId.get(deal.stageId))) return null;

  // (a) the promised close date is behind us — decide, do not let it rot.
  if (isPastDue(deal, now) && deal.expectedCloseAt) {
    const days = daysBetween(deal.expectedCloseAt, now);
    return {
      tone: 'destructive',
      // "minął 1 dni temu" is wrong in both languages — yesterday gets its own sentence.
      textKey: days === 1 ? 'overdueYesterday' : 'overdue',
      params: { days },
      actions: ['moveDate', 'won', 'lost'],
    };
  }

  // (b) nobody talked to the customer for a week.
  const idle = daysSinceActivity(
    { lastActivityAt: latestActivityAt(deal, activities), stageEnteredAt: deal.stageEnteredAt },
    now,
  );
  if (idle >= STALE_DAYS) {
    const actions: NextStepAction[] = input.contactHasPhone ? ['call', 'logCall'] : ['logCall'];
    return input.contactName
      ? { tone: 'amber', textKey: 'noContact', params: { days: idle, contact: input.contactName }, actions }
      : { tone: 'amber', textKey: 'noContactGeneric', params: { days: idle }, actions };
  }

  // (c) / (d) the forecast needs a value and a date.
  if (deal.valueNet === null) {
    return { tone: 'muted', textKey: 'noValue', params: {}, actions: ['enterValue'] };
  }
  if (deal.expectedCloseAt === null) {
    return { tone: 'muted', textKey: 'noDate', params: {}, actions: ['setDate'] };
  }

  // (e) on track — point at the next open stage, or at "won" from the last one.
  const index = stageIndex.open.findIndex((item) => item.id === deal.stageId);
  const next = index >= 0 ? stageIndex.open[index + 1] : undefined;
  if (next) {
    return {
      tone: 'emerald',
      textKey: 'onTrack',
      params: { stage: next.name },
      actions: ['moveNext'],
      nextStageId: next.id,
    };
  }
  return {
    tone: 'emerald',
    textKey: 'onTrack',
    params: { stage: stageIndex.won?.name ?? '' },
    actions: ['won'],
  };
}
